<template>
    <div>
        <CompassTable :size="CompassEnums.TableSize.MEDIUM">
            <CompassTableHead>
                <CompassTableHeadCell>
                    {{ $t.__('ocr.OcrVerificationForm.tax_rate') }}
                </CompassTableHeadCell>
                <CompassTableHeadCell>
                    {{ $t.__('ocr.OcrVerificationProductsTable.supplier_product') }}
                </CompassTableHeadCell>
                <CompassTableHeadCell class="ocr-tax-lines-table__header--align-right">
                    {{ $t.__('common.labels.amount_excluding_tax') }}
                </CompassTableHeadCell>
                <CompassTableHeadCell class="ocr-tax-lines-table__header--align-right">
                    {{ $t.__('common.labels.amount_including_tax') }}
                </CompassTableHeadCell>
                <CompassTableHeadCell>
                    {{ $t.__('ocr.OcrVerificationProductsTable.unit_optional') }}
                </CompassTableHeadCell>
                <CompassTableHeadCell>
                    {{ $t.__('common.labels.accounting_code') }}
                </CompassTableHeadCell>
                <CompassTableHeadCell v-if="props.showAnalyticCode">
                    {{ $t.__('ocr.OcrVerificationProductsTable.analytic_code_optional') }}
                </CompassTableHeadCell>
                <CompassTableHeadCell id="delete" fixed="right" :defaultWidth="80" />
            </CompassTableHead>
            <CompassTableBody>
                <OcrTaxLineRow
                    v-for="(field, index) in taxLinesArray.fields.value"
                    :key="field.key"
                    :index="index"
                    :disabled="props.disabled || props.isElectronic"
                    :canDelete="canDelete"
                    :taxRates="props.taxRates"
                    :accountingCodes="props.accountingCodes"
                    :units="props.units"
                    :showAnalyticCode="props.showAnalyticCode"
                    :currencySymbol="props.currencySymbol"
                    @request-delete="props.requestTaxLineDelete"
                />
            </CompassTableBody>
        </CompassTable>
        <CompassButton
            class="ocr-tax-lines-table__add-line"
            :label="$t.__('ocr.OcrVerificationForm.add_line')"
            :type="CompassEnums.ButtonType.TERTIARY"
            :disabled="props.disabled || props.isElectronic"
            leftIcon="plus"
            @click="props.addTaxLine"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue';
import { useFieldArray } from 'vee-validate';
import OcrTaxLineRow from 'vcomponents/ocr/OcrTaxLineRow.vue';
import type { OcrTaxLine } from 'composables/ocr/types';
import type { TaxRate } from 'types/entities/accounting/taxRate.type';
import type { AccountingCode } from 'types/entities/accounting/accountingCode.type';
import type { Unit } from 'types/entities/units/unit.type';
import type { Translator } from 'types/injects/translator.type';

interface Props {
    taxRates: TaxRate[];
    accountingCodes: AccountingCode[];
    units?: Unit[];
    showAnalyticCode?: boolean;
    currencySymbol?: string;
    disabled?: boolean;
    isElectronic?: boolean;
    addTaxLine: () => void;
    requestTaxLineDelete: (index: number) => void;
}

const props = withDefaults(defineProps<Props>(), {
    units: () => [],
    showAnalyticCode: false,
    currencySymbol: undefined,
    disabled: false,
    isElectronic: false,
});

const $t = inject('$t') as Translator;

const taxLinesArray = useFieldArray<OcrTaxLine>('taxLines');

const canDelete = computed<boolean>(() => taxLinesArray.fields.value.length > 1);
</script>

<style lang="scss" scoped>
.ocr-tax-lines-table {
    &__add-line {
        border: 0;
        padding-left: 0;
        margin-top: var(--spacing-2);
    }

    &__header--align-right {
        :deep(.compass-table__cell-tooltip) {
            justify-content: flex-end;
        }
    }
}
</style>
